import { withTrunkGateway } from "@/react/features/support-tools/siptrunk/withTrunkGateway";
import { CustomerCard } from "@/react/features/customer";
import React from "react";
import { AvailableLicenseWrapper } from "@/react/features/support-tools/siptrunk/components/AvailableLicenseWrapper";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";
import PageLayout from "@/react/features/styled/PageLayout";
import BreadcrumbsHeader from "@/react/features/support-tools/siptrunk/components/BreadcrumbsHeader";
import { useTrunkGatewayContext } from "@/react/features/support-tools/siptrunk/TrunkGatewayContext";
import styled from "styled-components";
import {
  FieldGroupContainer,
  IpBoxHeader,
  Label,
  StyledButton,
  StyledButtonGroup,
  StyledPlusIcon,
} from "../styled";
import {
  CREATE_TRUNK_GATEWAY_FIELDS,
  IP_BOXES_KEY,
} from "@/react/features/support-tools/siptrunk/constants";
import { TrunkGatewayInputField } from "@/react/features/support-tools/siptrunk/components/TrunkGatewayInputField";
import { IpBox } from "@/react/features/support-tools/siptrunk/components/IpBox";
import { TrunkGatewayCountrySelect } from "@/react/features/support-tools/siptrunk/components/TrunkGatewayCountrySelect";
import { COLORS } from "@/react/theme/constants";
import { Button } from "@/react/styled";
import { useCreateSipTrunkMutation } from "@/react/features/support-tools/siptrunk/api";
import { useLocation, useNavigate } from "react-router-dom";
import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";
import { errorNotification } from "@/react/notifications";
import { OverlaySpinner } from "@/react/components/OverlaySpinner";

const FormContainer = styled.div`
  max-width: 600px;
  > * {
    padding-bottom: 20px;
    border-bottom: 1px solid ${COLORS.tertiary};
  }
`;

const TrunkGatewayCreatePage = () => {
  const {
    state: trunkGateway,
    resetTrunkGateway,
    addIpBox,
    isValid,
  } = useTrunkGatewayContext();

  const [createSipTrunk, { isLoading: isCreating }] =
    useCreateSipTrunkMutation();
  const navigate = useNavigate();
  const location = useLocation();

  const createTrunkGateway = () => {
    createSipTrunk({ trunkGateway })
      .then((res) => {
        if (res.error) {
          notify({
            content: `Gateway could not be created: ${res.error?.data?.message}`,
            type: TYPE.error,
            duration: DURATION.INFINITE,
          });
        } else {
          notify({
            content: `Gateway ${trunkGateway.name} created successfully.`,
            type: TYPE.success,
            duration: 5000,
          });
          navigate(
            location.pathname.replace("/create", `/${res.data?.trunkId}`)
          );
        }
      })
      .catch((e) => errorNotification(e));
  };

  return (
    <PageLayout>
      <CustomerCard />
      <FullWidthDiv>
        {isCreating && <OverlaySpinner />}
        <AvailableLicenseWrapper>
          <BreadcrumbsHeader trunkGatewayName={"New Gateway"} />
          <FormContainer>
            <FieldGroupContainer>
              {CREATE_TRUNK_GATEWAY_FIELDS?.main.map((field) => (
                <TrunkGatewayInputField {...field} />
              ))}

              <IpBoxHeader>
                <Label>IP Boxes</Label>
                <StyledButton
                  variant={"text"}
                  onClick={() => addIpBox({ boxType: IP_BOXES_KEY.BOX })}
                >
                  <StyledPlusIcon /> Add IP Box
                </StyledButton>
              </IpBoxHeader>

              {trunkGateway &&
                (trunkGateway.boxes || []).map((box, index) => (
                  <IpBox boxType={IP_BOXES_KEY.BOX} index={index} {...box} />
                ))}

              <IpBoxHeader>
                <Label>Additional Outbound IP Boxes</Label>
                <StyledButton
                  variant={"text"}
                  onClick={() =>
                    addIpBox({ boxType: IP_BOXES_KEY.ADDITIONAL_BOX })
                  }
                >
                  <StyledPlusIcon /> Add IP Box
                </StyledButton>
              </IpBoxHeader>
              {trunkGateway &&
                (trunkGateway.additionalBoxes || []).map((box, index) => (
                  <IpBox
                    boxType={IP_BOXES_KEY.ADDITIONAL_BOX}
                    index={index}
                    {...box}
                  />
                ))}
            </FieldGroupContainer>

            <FieldGroupContainer>
              {CREATE_TRUNK_GATEWAY_FIELDS?.subscriberDetails.map((field) => (
                <TrunkGatewayInputField {...field} />
              ))}
              <TrunkGatewayCountrySelect />
            </FieldGroupContainer>
          </FormContainer>
          <StyledButtonGroup align="left">
            <Button
              variant={"primary"}
              onClick={createTrunkGateway}
              isDisabled={!isValid}
            >
              Save
            </Button>
            <Button variant={"secondary"} onClick={resetTrunkGateway}>
              Reset
            </Button>
          </StyledButtonGroup>
        </AvailableLicenseWrapper>
      </FullWidthDiv>
    </PageLayout>
  );
};

export default withTrunkGateway(TrunkGatewayCreatePage);
